import { memo, useMemo } from 'react'
import Button from '@/components/ui/Button'
import { RefreshCw, Sparkles, AlertCircle, TrendingUp, TrendingDown } from 'lucide-react'
import { useJiraPeopleOverview } from './hooks'
import JiraPerformanceStats from './components/JiraPerformanceStats'
import JiraCompletionTrendChart from './components/JiraCompletionTrendChart'
import JiraStatusDonutChart from './components/JiraStatusDonutChart'
import JiraPerformanceRadar from './components/JiraPerformanceRadar'
import JiraTeamLeaderboard from './components/JiraTeamLeaderboard'
import {
  calculateTeamMetrics,
  generateCumulativeTrend,
  calculateStatusDistribution,
  createTeamRadarData,
  createLeaderboard,
  getTopPerformers,
  getNeedsAttention,
  type TeamMetrics,
  type DailyMetric,
  type StatusDistribution,
  type RadarChartData,
  type LeaderboardEntry,
} from '@/lib/jira/metrics'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'

// ============================================================
// LOADING SKELETON
// ============================================================

const DashboardSkeleton = memo(function DashboardSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      {/* Stats Grid Skeleton */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="h-24 bg-gray-200 dark:bg-gray-700 rounded-xl" />
        ))}
      </div>

      {/* Charts Row Skeleton */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 h-80 bg-gray-200 dark:bg-gray-700 rounded-xl" />
        <div className="h-80 bg-gray-200 dark:bg-gray-700 rounded-xl" />
      </div>

      {/* Leaderboard Skeleton */}
      <div className="h-96 bg-gray-200 dark:bg-gray-700 rounded-xl" />
    </div>
  )
})

// ============================================================
// ERROR STATE
// ============================================================

const ErrorState = memo(function ErrorState({ 
  error, 
  onRetry 
}: { 
  error: string
  onRetry: () => void 
}) {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <Card variant="default" padding="lg" className="max-w-md text-center">
        <CardContent>
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
            <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Veri Yüklenemedi
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            {error}
          </p>
          <Button onClick={onRetry} leftIcon={<RefreshCw className="h-4 w-4" />}>
            Tekrar Dene
          </Button>
        </CardContent>
      </Card>
    </div>
  )
})

// ============================================================
// TOP PERFORMERS HIGHLIGHT
// ============================================================

const TopPerformersHighlight = memo(function TopPerformersHighlight({
  topPerformers,
  needsAttention,
}: {
  topPerformers: LeaderboardEntry[]
  needsAttention: LeaderboardEntry[]
}) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Top Performers */}
      <Card variant="default" padding="md" className="border-t-4 border-t-green-500">
        <CardHeader compact>
          <CardTitle compact className="flex items-center gap-2">
            <div className="p-1.5 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <TrendingUp className="h-4 w-4 text-green-600 dark:text-green-400" />
            </div>
            <span>En İyi Performans</span>
          </CardTitle>
        </CardHeader>
        <CardContent compact>
          {topPerformers.length > 0 ? (
            <div className="space-y-3">
              {topPerformers.map((entry) => (
                <div
                  key={entry.user.accountId}
                  className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-900/10 rounded-lg"
                >
                  <div className="flex-shrink-0">
                    {entry.user.avatarUrl ? (
                      <img
                        src={entry.user.avatarUrl}
                        alt={entry.user.displayName}
                        className="w-10 h-10 rounded-full"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center text-white font-medium">
                        {entry.user.displayName.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white truncate">
                      {entry.user.displayName}
                    </p>
                    <p className="text-xs text-green-600 dark:text-green-400">
                      %{entry.completionRate} tamamlama • {entry.velocity} görev/hafta
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-green-600 dark:text-green-400">
                      {entry.overallScore}
                    </p>
                    <p className="text-xs text-gray-400">puan</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-400 py-6">Henüz veri yok</p>
          )}
        </CardContent>
      </Card>

      {/* Needs Attention */}
      <Card variant="default" padding="md" className="border-t-4 border-t-orange-500">
        <CardHeader compact>
          <CardTitle compact className="flex items-center gap-2">
            <div className="p-1.5 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
              <TrendingDown className="h-4 w-4 text-orange-600 dark:text-orange-400" />
            </div>
            <span>Geliştirilmeli</span>
          </CardTitle>
        </CardHeader>
        <CardContent compact>
          {needsAttention.length > 0 ? (
            <div className="space-y-3">
              {needsAttention.map((entry) => (
                <div
                  key={entry.user.accountId}
                  className="flex items-center gap-3 p-3 bg-orange-50 dark:bg-orange-900/10 rounded-lg"
                >
                  <div className="flex-shrink-0">
                    {entry.user.avatarUrl ? (
                      <img
                        src={entry.user.avatarUrl}
                        alt={entry.user.displayName}
                        className="w-10 h-10 rounded-full"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center text-white font-medium">
                        {entry.user.displayName.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white truncate">
                      {entry.user.displayName}
                    </p>
                    <p className="text-xs text-orange-600 dark:text-orange-400">
                      %{entry.completionRate} tamamlama • {entry.openTasks} açık görev
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-orange-600 dark:text-orange-400">
                      {entry.overallScore}
                    </p>
                    <p className="text-xs text-gray-400">puan</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <Sparkles className="h-8 w-8 text-green-400 mx-auto mb-2" />
              <p className="text-sm text-green-600 dark:text-green-400">
                Harika! Tüm üyeler iyi performans gösteriyor.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
})

// ============================================================
// MAIN DASHBOARD COMPONENT
// ============================================================

const JiraPerformanceDashboard = memo(function JiraPerformanceDashboard() {
  const { data, isLoading, error, refresh, lastFetchTime } = useJiraPeopleOverview()

  // Hesaplanmış metrikler
  const teamMetrics = useMemo<TeamMetrics | null>(() => {
    if (!data) return null
    return calculateTeamMetrics(data)
  }, [data])

  const trendData = useMemo<DailyMetric[]>(() => {
    if (!data) return []
    return generateCumulativeTrend(data, 30)
  }, [data])

  const statusDistribution = useMemo<StatusDistribution[]>(() => {
    if (!data?.people) return []
    
    // Tüm kişilerin status'larını birleştir
    const combinedStatus: Record<string, number> = {}
    data.people.forEach(person => {
      Object.entries(person.tasksByStatus).forEach(([status, count]) => {
        combinedStatus[status] = (combinedStatus[status] || 0) + count
      })
    })
    
    return calculateStatusDistribution(combinedStatus)
  }, [data])

  const radarData = useMemo<RadarChartData[]>(() => {
    if (!data) return []
    return createTeamRadarData(data)
  }, [data])

  const leaderboard = useMemo<LeaderboardEntry[]>(() => {
    if (!data) return []
    return createLeaderboard(data)
  }, [data])

  const topPerformers = useMemo<LeaderboardEntry[]>(() => {
    if (!data) return []
    return getTopPerformers(data, 3)
  }, [data])

  const needsAttention = useMemo<LeaderboardEntry[]>(() => {
    if (!data) return []
    return getNeedsAttention(data, 3)
  }, [data])

  // Loading state
  if (isLoading && !data) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Jira Performans Dashboard
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Takım performansı ve metrikleri yükleniyor...
          </p>
        </div>
        <DashboardSkeleton />
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        <ErrorState error={error} onRetry={refresh} />
      </div>
    )
  }

  // No data state
  if (!data || !teamMetrics) {
    return null
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            🚀 Jira Performans Dashboard
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Takım performansı, verimlilik metrikleri ve trend analizi
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastFetchTime && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Son güncelleme: {new Date(lastFetchTime).toLocaleTimeString('tr-TR')}
            </span>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={refresh}
            loading={isLoading}
            leftIcon={<RefreshCw className="h-4 w-4" />}
          >
            Yenile
          </Button>
        </div>
      </div>

      {/* Performance Stats Grid */}
      <JiraPerformanceStats metrics={teamMetrics} loading={isLoading} />

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Completion Trend Chart - 2 column span */}
        <div className="lg:col-span-2">
          <JiraCompletionTrendChart
            data={trendData}
            title="Tamamlama Oranı Trendi (30 Gün)"
            height={280}
            loading={isLoading}
            showTarget
            targetValue={70}
          />
        </div>

        {/* Status Distribution - 1 column */}
        <JiraStatusDonutChart
          data={statusDistribution}
          title="Durum Dağılımı"
          height={200}
          loading={isLoading}
          showLegend
        />
      </div>

      {/* Radar & Highlights Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Performance Radar */}
        <JiraPerformanceRadar
          data={radarData}
          title="Takım Performans Analizi"
          height={280}
          loading={isLoading}
          showAverage
        />

        {/* Top Performers & Needs Attention - 2 column span */}
        <div className="lg:col-span-2">
          <TopPerformersHighlight
            topPerformers={topPerformers}
            needsAttention={needsAttention}
          />
        </div>
      </div>

      {/* Team Leaderboard */}
      <JiraTeamLeaderboard
        data={leaderboard}
        title="Takım Performans Sıralaması"
        loading={isLoading}
        maxItems={10}
      />

      {/* Footer */}
      <p className="text-xs text-center text-gray-400 dark:text-gray-500">
        Son güncelleme: {lastFetchTime ? new Date(lastFetchTime).toLocaleString('tr-TR') : '-'}
        {' • '}
        Veriler son 30 güne aittir
      </p>
    </div>
  )
})

export default JiraPerformanceDashboard

